import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, CartProvider, LangProvider } from "./lib/ctx";
import StoreLayout from "./components/store/StoreLayout";
import Home from "./pages/store/Home";
import Shop from "./pages/store/Shop";
import Product from "./pages/store/Product";
import Checkout from "./pages/store/Checkout";
import { PaymentResult, TrackOrder } from "./pages/store/Orders";
import GameTopUp from "./pages/store/GameTopUp";
import { BlogList, BlogPost, ContentPage } from "./pages/store/Content";
import { AuthPage, AuthCallback, ForgotPassword, ResetPassword } from "./pages/store/Auth";
import Account from "./pages/store/Account";
import Invoice from "./pages/store/Invoice";
import AdminLayout from "./pages/admin/AdminLayout";
import Dashboard, { Reports } from "./pages/admin/Dashboard";
import Products, { ProductEditor } from "./pages/admin/Products";
import Studio from "./pages/admin/Studio";
import { Inventory, Suppliers, PurchaseOrders, Pricing } from "./pages/admin/Ops";
import { Orders, Customers, GameTopupAdmin, Payments } from "./pages/admin/Sales";
import { Accounting, AISupport, NewsSEO, Marketing, Notifications, SettingsPage } from "./pages/admin/Back";
import ChangePassword from "./pages/admin/ChangePassword";

const PAGES = [["about", "about"], ["contact", "contact"], ["faqs", "faqs"], ["shipping-policy", "shipping"], ["returns", "returns"], ["privacy", "privacy"], ["terms", "terms"]];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route element={<StoreLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/shop/:category" element={<Shop />} />
        <Route path="/new-arrivals" element={<Shop />} />
        <Route path="/special-offers" element={<Shop />} />
        <Route path="/product/:slug" element={<Product />} />
        <Route path="/game-top-up" element={<GameTopUp />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/success" element={<PaymentResult />} />
        <Route path="/payment/cancel" element={<PaymentResult />} />
        <Route path="/track-order" element={<TrackOrder />} />
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        {PAGES.map(([p, k]) => <Route key={p} path={`/${p}`} element={<ContentPage page={k} />} />)}
        <Route path="/login" element={<AuthPage mode="login" />} />
        <Route path="/register" element={<AuthPage mode="register" />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/account" element={<Account />} />
        <Route path="/account/:tab" element={<Account />} />
        <Route path="*" element={<p className="p-20 text-center text-zinc-400">Page not found.</p>} />
      </Route>
      <Route path="/invoice/:number" element={<Invoice />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="products" element={<Products />} />
        <Route path="products/:id" element={<ProductEditor />} />
        <Route path="studio" element={<Studio />} />
        <Route path="inventory" element={<Inventory />} />
        <Route path="suppliers" element={<Suppliers />} />
        <Route path="purchase-orders" element={<PurchaseOrders />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="orders" element={<Orders />} />
        <Route path="customers" element={<Customers />} />
        <Route path="game-topup" element={<GameTopupAdmin />} />
        <Route path="shipping" element={<Orders shippingOnly />} />
        <Route path="payments" element={<Payments />} />
        <Route path="accounting" element={<Accounting />} />
        <Route path="ai-support" element={<AISupport />} />
        <Route path="news" element={<NewsSEO />} />
        <Route path="marketing" element={<Marketing />} />
        <Route path="reports" element={<Reports />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="password" element={<ChangePassword />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <CartProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster theme="dark" position="top-center" />
        </CartProvider>
      </AuthProvider>
    </LangProvider>
  );
}
